import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';
import CosmicWaves from '../components/CosmicWaves';

const LandingPage: React.FC = () => {
  // Statistics data
  const statistics = [
    { id: 3, label: '处理病历', value: '100万+' },
    { id: 4, label: '临床指南集成', value: '2000+' },
    { id: 5, label: '节省文书时间', value: '80%' },
    { id: 7, label: '医学文献覆盖', value: '2000万+' },
  ];

  // Specialty AI Assistants
  const specialtyAIAssistants = [
    {
      category: "心血管AI助理",
      icon: "Favorite",
      description: "基于ACC/AHA、ESC等权威指南训练，专注心血管疾病诊疗",
      features: [
        "实时解读心电图、超声等检查结果",
        "智能预警心衰风险，个性化用药建议",
        "房颤、冠心病等专病管理方案",
        "心血管介入手术辅助决策"
      ],
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      category: "肿瘤AI助理",
      icon: "Science",
      description: "深度集成NCCN、ESMO等指南，助力精准抗肿瘤治疗",
      features: [
        "多学科会诊方案智能生成",
        "个性化治疗方案推荐",
        "临床试验匹配筛选",
        "靶向药物指征分析"
      ],
      gradient: "from-indigo-500 to-purple-600"
    },
    {
      category: "内分泌AI助理",
      icon: "MonitorHeart",
      description: "基于ADA等指南，专注代谢性疾病管理",
      features: [
        "血糖管理个性化方案",
        "甲状腺结节智能分级",
        "代谢综合征风险评估",
        "内分泌失调诊断建议"
      ],
      gradient: "from-purple-500 to-pink-600"
    },
    {
      category: "呼吸AI助理",
      icon: "Air",
      description: "整合GOLD、GINA等指南，提供呼吸系统疾病诊疗支持",
      features: [
        "肺功能检查智能解读",
        "哮喘/COPD个性化治疗",
        "呼吸道感染鉴别诊断",
        "肺部影像AI辅助诊断"
      ],
      gradient: "from-teal-500 to-emerald-600"
    },
    {
      category: "消化AI助理",
      icon: "LocalHospital",
      description: "基于AASLD等指南，专注消化系统疾病诊疗",
      features: [
        "消化道内镜报告生成",
        "肝病诊疗方案制定",
        "IBD个性化治疗建议",
        "消化道肿瘤筛查预警"
      ],
      gradient: "from-orange-500 to-red-600"
    },
    {
      category: "神经AI助理",
      icon: "Psychology",
      description: "整合国际神经病学指南，提供专业诊疗决策支持",
      features: [
        "头痛诊疗路径推荐",
        "卒中溶栓适应症评估",
        "帕金森个性化治疗",
        "癫痫发作预警分析"
      ],
      gradient: "from-slate-600 to-slate-800"
    }
  ];

  // Problems data structure
  const problems = [
    {
      id: 1,
      problem: "临床工作超负荷",
      problemDetail: "医生平均每日处理40+病历，60%时间耗费在文书和协调工作。传统HIS系统移动端功能薄弱，关键信息获取不便。",
      solutionTitle: "智能临床工作流",
      solutionDetails: [
        "全平台病历即时访问：手机端同步查看检验报告、影像资料",
        "智能待办清单：自动聚合待处理医嘱、会诊请求",
        "语音病历生成：自然语言转结构化病历",
        "多科室协作看板：实时追踪会诊进度"
      ],
      icon: "Smartphone"
    },
    {
      id: 2,
      problem: "病历质量管控难",
      problemDetail: "初诊病历关键要素缺失率超30%，影响后续诊疗质量。传统质控滞后且标准不统一。",
      solutionTitle: "实时AI质控体系",
      solutionDetails: [
        "接诊时自动校验：现病史时序性/完整性检查",
        "智能补全建议：自动提示必要追问问题",
        "矛盾点预警：自动检测医嘱与诊断的矛盾",
        "质控知识库：内置3000+临床路径质控点"
      ],
      icon: "Checklist"
    },
    {
      id: 3,
      problem: "诊疗决策支持不足",
      problemDetail: "复杂病例平均需查阅5+文献，基层医院专家资源有限，治疗方案更新滞后。", 
      solutionTitle: "循证医学决策引擎",
      solutionDetails: [
        "AI辅助鉴别诊断：实时生成鉴别诊断树",
        "全球知识库联动：自动匹配最新临床试验",
        "虚拟多学科会诊：模拟专家讨论生成建议",
        "本院方案优化：基于历史成功案例推荐"
      ],
      icon: "Forum"
    },
    {
      id: 4,
      problem: "患者管理碎片化",
      problemDetail: "40%随访失访，慢病管理依从性不足50%。传统方式依赖人工追踪。",
      solutionTitle: "全周期患者管理",
      solutionDetails: [
        "AI病程摘要：自动生成诊疗里程碑时间轴",
        "智能随访引擎：自动配置随访计划",
        "风险预警看板：异常指标自动触发预警",
        "患者参与平台：检查预约、用药提醒"
      ],
      icon: "Timeline"
    }
  ];

  const advantages = [
    {
      id: 1,
      title: "智能诊疗引擎",
      icon: "PrecisionManufacturing",
      comparison: {
        traditional: {
          title: "传统方案",
          points: [
            "人工查阅病历，平均耗时40分钟",
            "依赖专家经验，诊疗水平不均衡",
            "知识更新滞后，难以及时应用新研究"
          ]
        },
        current: {
          title: "Agilis AI 方案",
          points: [
            "AI自动分析病历，3分钟完成",
            "标准化诊疗建议，辅助决策",
            "实时更新医学知识库"
          ]
        }
      }
    },
    {
      id: 2,
      title: "临床协作平台",
      icon: "Groups",
      comparison: {
        traditional: {
          title: "传统方案",
          points: [
            "线下会诊等待时间48小时+",
            "科室间沟通效率低",
            "信息传递容易断层"
          ]
        },
        current: {
          title: "Agilis AI 方案",
          points: [
            "远程会诊响应时间2小时内",
            "跨科室实时协作平台",
            "诊疗信息无缝对接"
          ]
        }
      }
    }
  ];

  const coreFeatures = [
    {
      id: 1,
      title: "医疗文档识别",
      icon: "DocumentScanner",
      description: "智能识别各类医疗文档，自动提取关键信息",
      features: [
        "支持20+种文档类型",
        "支持纸张、屏幕、仪器",
        "OCR准确率95%以上",
        "自动分类归档"
      ],
      gradient: "from-blue-600 to-indigo-700"
    },
    {
      id: 2,
      title: "AI辅助诊疗决策",
      icon: "Lightbulb",
      description: "智能分析病情，辅助临床决策",
      features: [
        "自动生成鉴别诊断树",
        "个性化治疗方案推荐",
        "用药合理性分析",
        "预后评估与风险预警"
      ],
      gradient: "from-indigo-600 to-purple-700"
    },
    {
      id: 3,
      title: "AI病程信息流",
      icon: "Timeline",
      description: "构建连续完整的患者病程记录",
      features: [
        "多渠道信息采集",
        "智能信息提取",
        "场景化信息视图",
        "院内院外全记录"
      ],
      gradient: "from-purple-600 to-pink-700"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header currentPage="chinese" />

      <div className="pt-16">
        {/* Hero Section */}
        <div className="relative overflow-hidden min-h-[600px] flex items-center ">
          <div className="absolute inset-0 z-0">
            <CosmicWaves speed={0.05} amplitude={0.8} frequency={1.0} starDensity={0.5} />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <div className="text-center animate-fadeIn">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                <span className="relative flex h-2 w-2 mr-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <p className="text-sm font-medium text-blue-50 tracking-wide uppercase">下一代智能临床工作站</p>
              </div>
              <h1 className="text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl mb-8">
                Agilis AI 智能临床解决方案
              </h1>
              <p className="mt-6 text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                为医疗专业人员提供智能工具，简化工作流程并节省 80% 的文档时间。提升人类专业知识，辅助而非替代。
              </p>
              <div className="mt-12 flex flex-col sm:flex-row justify-center gap-6">
                <Link
                  to="/register"
                  className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-primary-700 bg-white rounded-2xl shadow-xl hover:shadow-primary-500/25 hover:scale-105 transition-all duration-300"
                >
                  立即开启智能诊疗
                  <Icon iconName="ArrowForward" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-primary-600/30 backdrop-blur-md border-2 border-white/30 rounded-2xl hover:bg-white/10 transition-all duration-300"
                >
                  预约产品演示
                </Link>
              </div>
            </div>
          </div>

        </div>

        {/* Statistics Section */}
        <div className="relative -mt-12 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {statistics.map((stat, index) => (
                <div key={stat.id} 
                  className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 text-center animate-fadeIn"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="text-4xl font-black text-primary-600 mb-2">{stat.value}</div>
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-widest">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Features Section */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 animate-fadeIn">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Core Capabilities</h2>
              <h3 className="text-4xl font-bold text-slate-900 sm:text-5xl">核心功能模块</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {coreFeatures.map((feature, index) => (
                <div key={feature.id} 
                  className="group relative bg-white rounded-3xl border border-slate-100 p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 animate-fadeIn"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className={`inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white shadow-lg mb-8 group-hover:scale-110 transition-transform duration-500`}>
                    <Icon iconName={feature.icon} size={32} />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-4">{feature.title}</h4>
                  <p className="text-slate-600 mb-8 leading-relaxed">{feature.description}</p>
                  <ul className="space-y-4">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-center text-slate-700 font-medium">
                        <Icon iconName="CheckCircle" size={20} className="text-green-500 mr-3 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Specialty Assistants Section */}
        <div className="py-24 bg-slate-900 overflow-hidden relative">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-20">
              <h2 className="text-sm font-black text-primary-400 uppercase tracking-[0.3em] mb-4">Specialized AI</h2>
              <h3 className="text-4xl font-bold text-white sm:text-5xl">专科 AI 助理</h3>
              <p className="mt-6 text-xl text-slate-400 max-w-3xl mx-auto">
                深度集成各专科权威指南，提供专业精准的 AI 辅助诊疗服务
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialtyAIAssistants.map((specialty, index) => (
                <div key={index} 
                  className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700 p-8 rounded-3xl hover:bg-slate-800 transition-all duration-300"
                >
                  <div className={`inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br ${specialty.gradient} text-white mb-6 group-hover:rotate-12 transition-transform`}>
                    <Icon iconName={specialty.icon} size={24} />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">{specialty.category}</h4>
                  <p className="text-slate-400 text-sm mb-6 line-clamp-2">{specialty.description}</p>
                  <ul className="space-y-3">
                    {specialty.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-300">
                        <Icon iconName="Check" size={16} className="text-primary-400 mr-2 mt-0.5 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Problems & Solutions Section */}
        <div className="py-24 bg-slate-100/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Solutions</h2>
              <h3 className="text-4xl font-bold text-slate-900 sm:text-5xl">破解临床四大痛点</h3>
            </div>
            <div className="space-y-8">
              {problems.map((item, index) => (
                <div key={item.id} 
                  className={`flex flex-col lg:flex-row bg-white rounded-[2.5rem] overflow-hidden border border-slate-200/60 shadow-sm hover:shadow-2xl transition-all duration-500 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className="lg:w-1/2 p-12 bg-white">
                    <div className="flex items-center mb-6">
                      <div className="h-14 w-14 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center mr-4">
                        <Icon iconName={item.icon} size={28} />
                      </div>
                      <h4 className="text-3xl font-bold text-slate-900">{item.problem}</h4>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">{item.problemDetail}</p>
                  </div>
                  <div className="lg:w-1/2 p-12 bg-gradient-to-br from-slate-900 to-primary-900 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                      <Icon iconName={item.icon} size={200} className="text-white" />
                    </div>
                    <div className="relative z-10">
                      <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-bold mb-8">
                        {item.solutionTitle}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {item.solutionDetails.map((detail, idx) => (
                          <div key={idx} className="flex items-start text-white/90 font-medium">
                            <Icon iconName="MdCheckCircle" size={24} className="text-green-400 mr-3 shrink-0" />
                            {detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Advantages Comparison */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Comparison</h2>
              <h3 className="text-4xl font-bold text-slate-900 sm:text-5xl">为什么选择 Agilis AI</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {advantages.map((advantage) => (
                <div key={advantage.id} className="bg-slate-100/40 rounded-[3rem] border border-slate-200/60 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-r from-slate-900 to-primary-900 p-8 flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon iconName={advantage.icon} size={32} className="text-white mr-4" />
                      <h4 className="text-2xl font-bold text-white">{advantage.title}</h4>
                    </div>
                  </div>
                  <div className="p-10 grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <div className="space-y-6">
                      <h5 className="flex items-center text-slate-400 font-bold uppercase tracking-widest text-xs">
                        <Icon iconName="History" size={16} className="mr-2" />
                        {advantage.comparison.traditional.title}
                      </h5>
                      <ul className="space-y-4">
                        {advantage.comparison.traditional.points.map((point, idx) => (
                          <li key={idx} className="flex items-start text-sm text-slate-500 italic">
                            <Icon iconName="Remove" size={18} className="text-slate-300 mr-2 shrink-0 mt-0.5" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-6">
                      <h5 className="flex items-center text-primary-600 font-bold uppercase tracking-widest text-xs">
                        <Icon iconName="Bolt" size={16} className="mr-2" />
                        {advantage.comparison.current.title}
                      </h5>
                      <ul className="space-y-4">
                        {advantage.comparison.current.points.map((point, idx) => (
                          <li key={idx} className="flex items-start text-sm text-slate-900 font-bold">
                            <Icon iconName="CheckCircle" size={18} className="text-green-500 mr-2 shrink-0 mt-0.5" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-slate-900 to-primary-900 rounded-[4rem] p-12 lg:p-20 relative overflow-hidden text-center lg:text-left">
              <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
              <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-16">
                <div className="lg:w-1/2">
                  <h3 className="text-4xl font-bold text-white mb-8">企业级数据安全与隐私保护</h3>
                  <p className="text-xl text-slate-300 leading-relaxed mb-12">
                    采用 AES-256 加密、TLS 1.3 传输、本地化存储及私有化部署方案，确保患者数据万无一失。
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                      <Icon iconName="Lock" size={24} className="text-amber-500 mb-4" />
                      <h5 className="text-white font-bold mb-2">全链路加密</h5>
                      <p className="text-slate-400 text-sm">传输与存储全过程加密保护</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                      <Icon iconName="Shield" size={24} className="text-amber-500 mb-4" />
                      <h5 className="text-white font-bold mb-2">隐私脱敏</h5>
                      <p className="text-slate-400 text-sm">敏感信息自动脱敏处理</p>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/3 relative">
                  <div className="relative z-10 bg-white p-8 rounded-3xl shadow-2xl">
                    <Icon iconName="VerifiedUser" size={80} className="text-amber-600 mx-auto mb-6" />
                    <h4 className="text-2xl font-black text-slate-900 text-center mb-4">合规认证</h4>
                    <p className="text-slate-500 text-center">符合三级等保要求及医疗数据隐私保护标准</p>
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary-500/20 blur-[100px] rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-24 bg-slate-100/30 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative bg-gradient-to-br from-primary-700 to-primary-900 rounded-[3rem] p-12 lg:p-24 text-center overflow-hidden">
              <div className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              <div className="relative z-10 animate-fadeIn">
                <h2 className="text-4xl font-bold text-white sm:text-6xl mb-8">准备好提升您的诊疗效率了吗？</h2>
                <p className="text-xl text-primary-100 mb-12 max-w-2xl mx-auto font-medium">
                  立即加入领先的医疗团队，体验 Agilis AI 带来的智慧医疗变革。
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link
                    to="/register"
                    className="px-10 py-5 bg-white text-primary-700 text-xl font-bold rounded-2xl hover:scale-105 transition-transform shadow-2xl"
                  >
                    免费开始使用
                  </Link>
                  <Link
                    to="/home-care-cn"
                    className="px-10 py-5 bg-primary-700/50 text-white text-xl font-bold rounded-2xl hover:bg-primary-700 border border-white/20 transition-all backdrop-blur-md"
                  >
                    切换到个人版
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default LandingPage;
